import {useCallback, useEffect, useRef, useState} from 'react';

import {useMap} from '@vis.gl/react-google-maps';

import useAutoRecenter from '~/shared/components/Maps/useAutoRecenter';
import {Coordinate, MapModeEnum} from '~/shared/components/Maps/types';
import {MapView} from '~/shared/components/Maps/index';
import {SupportedQueryParamsType} from '~/shared/router/supportedQueryParams';
import {INITIAL_MAP_CENTER} from '~/shared/components/Maps/consts';
import {compareCoordinates} from '~/shared/components/Maps/utils';

type TrackerMapProps = {
  mode: MapModeEnum;
  courierLocation?: Coordinate | null;
  businessLocation: Coordinate | null;
  onBusinessMarkerClick?: () => void;
  userLocation?: Coordinate | null;
  onUserMarkerClick?: () => void;
  dotsOfLastRoute?: Coordinate[];
  maxZoom?: number;
  debug?: SupportedQueryParamsType['debug'];
};

const TrackerMap = ({
  mode,
  courierLocation,
  businessLocation,
  onBusinessMarkerClick,
  userLocation,
  onUserMarkerClick,
  dotsOfLastRoute,
  maxZoom,
  debug,
}: TrackerMapProps) => {
  const [showCenterButton, setShowCenterButton] = useState(false);
  const _map = useMap();
  const isInitiallyCentered = useRef(false);

  const onRecenterSuccessCallback = useCallback(() => {
    setShowCenterButton(false);
  }, []);

  const {onRecenter, onZoomOrCenterChange, optimalCenter, optimalZoom} = useAutoRecenter({
    map: _map,
    mode,
    isCourierLocationUsed: !!courierLocation,
    userLocation,
    businessLocation,
    courierLocation,
    onRecenterSuccessCallback,
    maxZoom,
  });

  useEffect(() => {
    if (isInitiallyCentered.current || !_map) {
      return;
    }
    // the default center has to be re-defined (til api response is received)
    onRecenter();

    const isInitCenter = compareCoordinates(INITIAL_MAP_CENTER, optimalCenter);
    if (!isInitCenter) {
      isInitiallyCentered.current = true;
    }
  }, [_map, onRecenter, optimalCenter]);

  const handleCenterChanged = ({detail: {center, zoom}}: any) => {
    const isMapCenteredToDefault = compareCoordinates(center, optimalCenter);
    const isZoomedToDefault = zoom === optimalZoom;
    setShowCenterButton(!isMapCenteredToDefault || !isZoomedToDefault);

    if (!isMapCenteredToDefault || !isZoomedToDefault) {
      onZoomOrCenterChange();
      setShowCenterButton(true);
    }
  };

  return (
    <MapView
      defaultCenter={optimalCenter}
      defaultZoom={optimalZoom}
      onCenterChanged={handleCenterChanged}
      businessLocation={businessLocation}
      onBusinessMarkerClick={onBusinessMarkerClick}
      userLocation={userLocation}
      onUserMarkerClick={onUserMarkerClick}
      courierLocation={courierLocation}
      dotsOfLastRoute={dotsOfLastRoute}
      onRecenter={onRecenter}
      showCenterButton={showCenterButton}
      debug={debug}
    />
  );
};

export default TrackerMap;
